import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { MozartReCharts } from '@pgforsta/mozart_ui';
import { MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import Layout from '../Layout/Layout';
import { Card, CardHeader, CardContent } from '../../components/styled/Card';
import ButtonGroup from '../../components/styled/ButtonGroup';
import * as dashboardService from '../../api/services/dashboard';
import useAsync from '../../hooks/async';

const Boxes = styled.div`
  display: flex;
  align-items: center;
`;

const Box = styled.div`
  width: 100%;
  padding: 20px;
  border: 1px solid #d9d5f9;
  border-radius: 10px;

  &:not(:last-child) {
    margin-right: 20px;
  }
`;

const BoxIcon = styled.div`
  box-sizing: border-box;
  width: 50px;
  height: 50px;
  margin-bottom: 20px;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 99px;
  background-color: #6c60f6;

  i {
    color: white;
    font-size: 22px;
  }
`;

const BoxLabel = styled.div`
  margin-bottom: 10px;
  color: #3e3a83;
  font-size: 14px;
`;

const BoxType = styled.div`
  margin-bottom: 10px;
  color: #3f3b84;
  font-size: 18px;
`;

const BoxAmount = styled.div`
  color: #6c60f6;
  font-size: 28px;
`;

const Charts = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: flex-start;

  & > * {
    width: 100%;

    &:not(:last-child) {
      margin-right: 20px;
      width: 100%;
    }
  }

  .recharts-responsive-container {
    margin: 0 auto;
  }
`;

const ChartLabels = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 20px;
  column-gap: 40px;
`;

const ChartLabel = styled.div`
  display: flex;
  align-items: center;
`;

const ChartColorBadge = styled.span`
  width: 10px;
  height: 10px;
  margin-right: 10px;
  display: inline-block;
  border-radius: 2px;
  background-color: ${(props) => props.color};
`;

const ChartLabelText = styled.span`
  font-size: 18px;
  font-weight: 600;
`;

const ChartLabelPercentage = styled.span`
  margin-left: auto;
  display: inline-block;
  font-size: 18px;
`;

const ChartText = styled.div`
  text-align: center;
`;

// TODO add new color for "Others"
const colors = [
  '#85abf9',
  '#f09491',
  '#7f5398',
  '#95d5d8',
  '#f9d365',
  '#8fa3ae',
  '#ec5b8f',
  '#a79df4',
  '#67b4f6',
  '#b459c6',
  '#bad0fc',
];

const Dashboard = () => {
  const history = useHistory();

  const urls = useAsync();
  const locations = useAsync();
  const providers = useAsync();

  const { run: runUrls } = urls;
  const { run: runLocations } = locations;
  const { run: runProviders } = providers;

  useEffect(() => {
    runUrls(dashboardService.getUrlsData());
  }, [runUrls]);

  useEffect(() => {
    runLocations(dashboardService.getLocationsSources());
  }, [runLocations]);

  useEffect(() => {
    runProviders(dashboardService.getProvidersSources());
  }, [runProviders]);

  const getChartsConfig = () => ({
    chartColors: colors,
    cxWidth: 127,
    cyHeight: 127,
    innerRadius: 60,
    outerRadius: 130,
    width: 265,
    height: 265,
    legend: { show: false },
  });

  const getChartData = (sourceData) =>
    sourceData.map((item) => ({
      name: item.sourceName,
      value: item.percentage,
    }));

  const renderActions = () => {
    return (
      <ButtonGroup>
        <MozartButton
          icon="icon-VerifyURL"
          onClick={() => history.push('/entity-listing')}
        >
          Verify URLs
        </MozartButton>
        <MozartButton
          icon="icon-Crawlerconfig"
          onClick={() => history.push('/crawler-config')}
        >
          Crawler Config
        </MozartButton>
      </ButtonGroup>
    );
  };

  const renderBoxes = () => {
    if (urls.status !== 'resolved') {
      return null;
    }

    const values = [
      { id: 'urlsAutoVerified', label: 'AUTO-VERIFIED', icon: 'icon-Complete' },
      { id: 'urlsCrawled', label: 'CRAWLED', icon: 'icon-VerifyURL' },
      { id: 'urlsVerified', label: 'VERIFIED', icon: 'icon-Complete' },
    ];

    // TODO add missing icon
    return values.map(({ id, label, icon }) => (
      <Box key={id}>
        <BoxIcon>
          <i className={icon} />
        </BoxIcon>
        <BoxLabel>Number of</BoxLabel>
        <BoxType>{`URLs ${label}`}</BoxType>
        <BoxAmount>{urls.data[id].toLocaleString()}</BoxAmount>
      </Box>
    ));
  };

  const renderChartLabels = (sourceData) =>
    sourceData.map((item, index) => (
      <ChartLabel key={item.sourceName}>
        <ChartColorBadge color={colors[index % colors.length]} />
        <ChartLabelText>{item.sourceName}</ChartLabelText>
        <ChartLabelPercentage>{`${
          item.percentage < 1 ? '<1' : item.percentage
        }%`}</ChartLabelPercentage>
      </ChartLabel>
    ));

  const renderChart = (sourceData, status, id) => {
    if (status === 'idle' || status === 'pending') {
      return <ChartText>Loading...</ChartText>;
    }

    if (
      status === 'rejected' ||
      (status === 'resolved' && sourceData.length === 0)
    ) {
      return <ChartText>No data available</ChartText>;
    }

    return (
      <>
        <MozartReCharts
          id={id}
          chartType="PieChart"
          config={getChartsConfig()}
          data={getChartData(sourceData)}
        />
        <ChartLabels>{renderChartLabels(sourceData)}</ChartLabels>
      </>
    );
  };

  const locationsSourceData = Array.isArray(locations.data?.locationSourceData)
    ? locations.data.locationSourceData
    : [];
  const providerSourceData = Array.isArray(providers.data?.providersSourceData)
    ? providers.data?.providersSourceData
    : [];

  return (
    <Layout title="Dashboard" actions={renderActions()}>
      <Boxes className="boxes">{renderBoxes()}</Boxes>
      <Charts>
        <Card>
          <CardHeader>Top 10 Sources of Locations</CardHeader>
          <CardContent>
            {renderChart(
              locationsSourceData,
              locations.status,
              'Top10LocationSources'
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>Top 10 Sources of Providers</CardHeader>
          <CardContent>
            {renderChart(
              providerSourceData,
              providers.status,
              'Top10ProviderSources'
            )}
          </CardContent>
        </Card>
      </Charts>
    </Layout>
  );
};

export default Dashboard;
